// Pure shaping for the sidebar: how a workspace list becomes projects, and how
// a timestamp becomes an age. Both live here rather than in the component so
// they can be tested by `pnpm test` without rendering anything.

import type { Workspace } from './runner';

/**
 * Bucket workspaces under their project, in the order the runner sent them.
 *
 * Order is the registry file's order all the way through — Python dicts, JSON,
 * and this Map all preserve insertion — so whoever edits the registry decides
 * what the sidebar looks like, and a project's repos have to sit together in the
 * file. That's a real constraint, and cheaper than teaching the UI to sort by a
 * priority nobody wants to maintain.
 *
 * A workspace with no project falls under "Other" rather than vanishing: an
 * older runner omits the field entirely, and one flat group is a fair way to
 * degrade.
 */
export function byProject(all: Workspace[]): [string, Workspace[]][] {
  const groups = new Map<string, Workspace[]>();
  for (const ws of all) {
    const key = ws.project?.trim() || 'Other';
    const bucket = groups.get(key);
    if (bucket) bucket.push(ws);
    else groups.set(key, [ws]);
  }
  return [...groups];
}

/**
 * What to call the projects a conversation was opened with, in one line.
 *
 * Two are named outright, because two is the common pair — an engine and its
 * site, a repo and the wiki — and naming them is the whole use of the line.
 * Past that it counts, since four names in a row under a title is a list nobody
 * reads and it pushes the title out of the way.
 *
 * "no project" rather than "" or "home": an empty choice is a real state that
 * someone deliberately started, and a blank where every other row has a word
 * reads as something failing to load.
 */
export function whereLabel(names: string[]): string {
  if (!names.length) return 'no project';
  if (names.length <= 2) return names.join(' + ');
  return `${names[0]} + ${names.length - 1} more`;
}

/**
 * A timestamp as a glanceable age: "3m", "2h", "yesterday", "28 Jul".
 *
 * `now` is a parameter so this is a pure function of its inputs and can be
 * tested without freezing the clock. Callers leave it out.
 *
 * Only ever rendered on the client — the sidebar exists only behind a token, and
 * the server always renders the locked state — so there is no server/client
 * hydration mismatch to design around.
 */
/**
 * A git commit stamp — unix seconds — as the same glanceable age, or '' when
 * there is none.
 *
 * Empty rather than "unknown" or "never": an untracked file simply has no date
 * to show, and saying that in words next to a filename would give the absence
 * more weight than the date itself ever earns.
 */
export function commitAge(unix: number | null, now?: number): string {
  if (unix === null) return '';
  return ago(new Date(unix * 1000).toISOString(), now);
}

/**
 * Cut a list of conversations into the spans people actually think in.
 *
 * The sidebar's list was a flat run of rows carrying an age each, which reads as
 * one undifferentiated column: the jump from "3m" to "5h" is the real edge in a
 * day's work and nothing marked it. Headings put that edge on screen and cost
 * one line per span.
 *
 * Calendar days rather than elapsed hours, because "yesterday" means the day
 * before this one and not twenty-five hours ago — at nine in the morning, work
 * from last night is yesterday's whatever the arithmetic says. `Earlier`
 * swallows everything past the week rather than growing a heading per month:
 * the list is sorted by recency, so the tail is where you go when you already
 * know what you are looking for, and more headings down there is more to read
 * past on the way to the top.
 *
 * Empty spans never appear. Order is the order given, which the runner has
 * already sorted by last activity.
 */
export function bySpan<T>(
  items: T[],
  when: (item: T) => string,
  now: number = Date.now(),
): [string, T[]][] {
  const startOfToday = new Date(now).setHours(0, 0, 0, 0);
  const day = 86_400_000;

  const label = (iso: string): string => {
    const then = Date.parse(iso);
    // Unparseable, or stamped in the future by a clock that disagrees: put it at
    // the top rather than inventing a span for it.
    if (!Number.isFinite(then) || then >= startOfToday) return 'Today';
    if (then >= startOfToday - day) return 'Yesterday';
    if (then >= startOfToday - 6 * day) return 'This week';
    return 'Earlier';
  };

  const spans = new Map<string, T[]>();
  for (const item of items) {
    const key = label(when(item));
    const bucket = spans.get(key);
    if (bucket) bucket.push(item);
    else spans.set(key, [item]);
  }
  return [...spans];
}

export function ago(iso: string, now: number = Date.now()): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '';
  const mins = Math.floor((now - then) / 60000);
  // A clock that disagrees with the server's, or a stamp written a moment ago
  // and read before the minute ticks, both land here. "just now" is true enough
  // and a negative age never reaches the screen.
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  if (hours < 48) return 'yesterday';
  return new Date(then).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
